import React from 'react';
import { FaShoppingCart, FaCheck, FaPlus, FaMinus } from 'react-icons/fa';
import { useCart } from '../../context/CartContext';
import { useStoreStatus } from '../../context/StoreStatusContext';
import {
  buildCartLineFromVariant,
  isVariableMenuItem,
} from './ourMenuUtils';

const ADD_LABEL = {
  card: ' ADD TO CART',
  list: ' ADD TO CART',
  color: ' ADD TO CART',
  compact: '',
  modal: ' ADD TO CART',
};

const ADDED_LABEL = {
  card: ' ADDED',
  list: ' ADDED',
  color: ' ADDED',
  compact: '',
  modal: ' ADDED TO CART',
};

/**
 * Add-to-cart CTA that switches to cart-style +/- quantity when the item is already in the cart.
 * Variable products: opens options via onOpenOptions unless a variant is already resolved (modal).
 * Never qty-steps against a parent id — only child SKUs after option selection.
 */
export default function MenuCartAction({
  item,
  orderable = true,
  unavailableLabel = 'Unavailable',
  variant = 'card',
  className = '',
  addedClassName = '',
  onAfterAdd,
  onOpenOptions,
  /** When set (modal after option pick), cart uses this child SKU. */
  selectedVariant = null,
}) {
  const { cartItems, addToCart, updateQty, addedId } = useCart();
  const { confirmAddWhileClosed } = useStoreStatus();

  const variable = isVariableMenuItem(item);
  const needsOptions =
    variable && !selectedVariant && typeof onOpenOptions === 'function';

  // Variable without a chosen option must not use the parent as a cart line.
  const cartPayload =
    selectedVariant
      ? buildCartLineFromVariant(item, selectedVariant)
      : variable
        ? null
        : {
            id: item.id,
            name: item.name,
            price: item.price,
            priceDisplay: item.priceDisplay,
            image: item.image,
            category: item.category,
          };

  const cartLineId = cartPayload?.id;
  const cartLine =
    cartLineId != null
      ? (cartItems || []).find((row) => String(row.id) === String(cartLineId))
      : null;
  const quantity = Math.max(0, Number(cartLine?.quantity) || 0);
  const justAdded = addedId != null && cartLineId != null && String(addedId) === String(cartLineId);

  const addToRegularCart = async () => {
    if (needsOptions) {
      onOpenOptions(item, { intent: 'cart' });
      return;
    }
    if (!cartPayload) return;
    const ok = await confirmAddWhileClosed();
    if (!ok) return;
    addToCart(cartPayload);
    if (typeof onAfterAdd === 'function') onAfterAdd();
  };

  const increaseQty = async () => {
    if (cartLineId == null) return;
    const ok = await confirmAddWhileClosed();
    if (!ok) return;
    updateQty(cartLineId, quantity + 1);
  };

  if (!orderable) {
    return (
      <button
        type="button"
        className={`${className} menu-item__cta--oos`.trim()}
        disabled
        aria-disabled="true"
      >
        {unavailableLabel}
      </button>
    );
  }

  // Only show qty stepper for a resolved cart line (simple item or selected child SKU).
  if (!needsOptions && cartPayload && quantity > 0) {
    return (
      <div
        className={`menu-qty-stepper menu-qty-stepper--cart menu-qty-stepper--${variant}`}
        role="group"
        aria-label={`Regular cart quantity for ${item.name}`}
        title="Regular cart"
      >
        <button
          type="button"
          className="menu-qty-stepper__btn"
          onClick={() => updateQty(cartLineId, quantity - 1)}
          aria-label={`Decrease cart quantity of ${item.name}`}
        >
          <FaMinus />
        </button>
        <span className="menu-qty-stepper__value" aria-live="polite">
          <span className="menu-qty-stepper__kind">
            <FaShoppingCart aria-hidden />
            <span className="menu-qty-stepper__kind-text">Cart</span>
          </span>
          <span className="menu-qty-stepper__count">{quantity}</span>
        </span>
        <button
          type="button"
          className="menu-qty-stepper__btn"
          onClick={increaseQty}
          aria-label={`Increase cart quantity of ${item.name}`}
        >
          <FaPlus />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      className={`${className}${justAdded && addedClassName ? ` ${addedClassName}` : ''}`.trim()}
      onClick={addToRegularCart}
      aria-label={
        justAdded
          ? 'Added to cart'
          : needsOptions
            ? `Add ${item.name} to cart — choose an option`
            : `Add ${item.name} to cart`
      }
    >
      {justAdded ? (
        <>
          <FaCheck />
          {ADDED_LABEL[variant] ?? ADDED_LABEL.card}
        </>
      ) : (
        <>
          <FaShoppingCart />
          {ADD_LABEL[variant] ?? ADD_LABEL.card}
        </>
      )}
    </button>
  );
}
